/**
 * Drawer Analytics
 *
 * Tracks user interactions with the drawer system for analytics purposes.
 * Provides insights into drawer usage patterns, engagement, and performance.
 *
 * @see docs/stories/DRAWER-5.3-add-analytics-instrumentation.md
 */

import type { DrawerType } from "@/types/drawer";

// ============================================================================
// Types
// ============================================================================

export interface DrawerOpenEvent {
  drawerType: DrawerType;
  layer: number;
  projectId?: string;
  timestamp: number;
  source: "quick_access" | "context_menu" | "keyboard" | "programmatic";
}

export interface DrawerCloseEvent {
  drawerType: DrawerType;
  layer: number;
  durationMs: number;
  hadUnsavedChanges: boolean;
  wasDiscarded: boolean;
  timestamp: number;
}

export interface DrawerInteractionEvent {
  drawerType: DrawerType;
  action: "form_submit" | "copy_value" | "navigate" | "scroll";
  metadata?: Record<string, unknown>;
  timestamp: number;
}

export interface DrawerErrorEvent {
  drawerType: DrawerType;
  error: string;
  context?: Record<string, unknown>;
  timestamp: number;
}

export interface DrawerMetrics {
  /** Average time drawers are kept open (ms) */
  avgDurationMs: number;
  /** Total number of drawer opens */
  totalOpens: number;
  /** Opens by drawer type */
  opensByType: Record<DrawerType, number>;
  /** Discard rate (unsaved changes discarded / total closes) */
  discardRate: number;
  /** Max drawer depth reached */
  maxDepthReached: number;
}

// ============================================================================
// State
// ============================================================================

// Track open drawers for duration calculation
const openDrawers = new Map<string, DrawerOpenEvent>();

// Track metrics for session
let sessionMetrics: DrawerMetrics = {
  avgDurationMs: 0,
  totalOpens: 0,
  opensByType: {} as Record<DrawerType, number>,
  discardRate: 0,
  maxDepthReached: 0,
};

let totalDurationMs = 0;
let totalCloses = 0;
let totalDiscards = 0;

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Track drawer opened event
 */
export function trackDrawerOpen(event: Omit<DrawerOpenEvent, "timestamp">): void {
  const fullEvent: DrawerOpenEvent = {
    ...event,
    timestamp: Date.now(),
  };

  // Store for duration tracking
  const key = `${event.drawerType}-${event.layer}`;
  openDrawers.set(key, fullEvent);

  // Update metrics
  sessionMetrics.totalOpens++;
  sessionMetrics.opensByType[event.drawerType] =
    (sessionMetrics.opensByType[event.drawerType] || 0) + 1;
  sessionMetrics.maxDepthReached = Math.max(
    sessionMetrics.maxDepthReached,
    event.layer
  );

  // Log for debugging (replace with actual analytics in production)
  if (process.env.NODE_ENV === "development") {
    console.debug("[DrawerAnalytics] Opened:", fullEvent);
  }

  // Send to analytics service
  sendAnalyticsEvent("drawer_opened", fullEvent);
}

/**
 * Track drawer closed event
 */
export function trackDrawerClose(
  drawerType: DrawerType,
  layer: number,
  hadUnsavedChanges: boolean,
  wasDiscarded: boolean
): void {
  const key = `${drawerType}-${layer}`;
  const openEvent = openDrawers.get(key);

  const durationMs = openEvent ? Date.now() - openEvent.timestamp : 0;

  const event: DrawerCloseEvent = {
    drawerType,
    layer,
    durationMs,
    hadUnsavedChanges,
    wasDiscarded,
    timestamp: Date.now(),
  };

  // Clean up tracking
  openDrawers.delete(key);

  // Update metrics
  totalCloses++;
  totalDurationMs += durationMs;
  if (wasDiscarded) totalDiscards++;

  sessionMetrics.avgDurationMs = totalDurationMs / totalCloses;
  sessionMetrics.discardRate = totalDiscards / totalCloses;

  // Log for debugging
  if (process.env.NODE_ENV === "development") {
    console.debug("[DrawerAnalytics] Closed:", event);
  }

  // Send to analytics service
  sendAnalyticsEvent("drawer_closed", event);
}

/**
 * Track drawer interaction (form submit, copy, navigate, etc.)
 */
export function trackDrawerInteraction(
  event: Omit<DrawerInteractionEvent, "timestamp">
): void {
  const fullEvent: DrawerInteractionEvent = {
    ...event,
    timestamp: Date.now(),
  };

  // Log for debugging
  if (process.env.NODE_ENV === "development") {
    console.debug("[DrawerAnalytics] Interaction:", fullEvent);
  }

  // Send to analytics service
  sendAnalyticsEvent("drawer_interaction", fullEvent);
}

/**
 * Track drawer error
 */
export function trackDrawerError(
  event: Omit<DrawerErrorEvent, "timestamp">
): void {
  const fullEvent: DrawerErrorEvent = {
    ...event,
    timestamp: Date.now(),
  };

  // Always log errors
  console.error("[DrawerAnalytics] Error:", fullEvent);

  // Send to analytics service
  sendAnalyticsEvent("drawer_error", fullEvent);
}

/**
 * Track max drawers reached (attempted to open 4th drawer)
 */
export function trackMaxDrawersReached(): void {
  if (process.env.NODE_ENV === "development") {
    console.debug("[DrawerAnalytics] Max drawers reached");
  }

  sendAnalyticsEvent("drawer_max_reached", { timestamp: Date.now() });
}

/**
 * Track duplicate drawer detection (user tried to open already-open drawer)
 */
export function trackDuplicateDrawer(drawerType: DrawerType): void {
  if (process.env.NODE_ENV === "development") {
    console.debug("[DrawerAnalytics] Duplicate drawer:", drawerType);
  }

  sendAnalyticsEvent("drawer_duplicate_detected", {
    drawerType,
    timestamp: Date.now(),
  });
}

// ============================================================================
// Metrics Access
// ============================================================================

/**
 * Get current session metrics
 */
export function getSessionMetrics(): DrawerMetrics {
  return { ...sessionMetrics };
}

/**
 * Reset session metrics (e.g., on logout)
 */
export function resetSessionMetrics(): void {
  sessionMetrics = {
    avgDurationMs: 0,
    totalOpens: 0,
    opensByType: {} as Record<DrawerType, number>,
    discardRate: 0,
    maxDepthReached: 0,
  };
  totalDurationMs = 0;
  totalCloses = 0;
  totalDiscards = 0;
  openDrawers.clear();
}

// ============================================================================
// Analytics Service Integration
// ============================================================================

/**
 * Send event to analytics service.
 * This is a placeholder - integrate with your actual analytics provider.
 *
 * Common providers:
 * - Mixpanel: mixpanel.track(eventName, properties)
 * - Amplitude: amplitude.logEvent(eventName, properties)
 * - PostHog: posthog.capture(eventName, properties)
 * - Google Analytics: gtag('event', eventName, properties)
 */
function sendAnalyticsEvent<T extends object>(
  eventName: string,
  properties: T
): void {
  // Check for analytics provider
  const analytics = (window as unknown as { analytics?: { track: (name: string, props: unknown) => void } }).analytics;

  if (analytics?.track) {
    analytics.track(eventName, properties);
    return;
  }

  // Fallback: PostHog
  const posthog = (window as unknown as { posthog?: { capture: (name: string, props: unknown) => void } }).posthog;
  if (posthog?.capture) {
    posthog.capture(eventName, properties);
    return;
  }

  // Fallback: Google Analytics
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (gtag) {
    gtag("event", eventName, properties);
    return;
  }

  // In development, we already logged above
  // In production without analytics, this is a no-op
}

export default {
  trackDrawerOpen,
  trackDrawerClose,
  trackDrawerInteraction,
  trackDrawerError,
  trackMaxDrawersReached,
  trackDuplicateDrawer,
  getSessionMetrics,
  resetSessionMetrics,
};
